import ApiService from "@/services/ApiService";
import { ROTAS_API_PAINEL } from "../types/painelConsultor.api.types";
import type {
  AtaDetalheApi,
  AtasPainelApi,
  CarteiraPainelApi,
  ChamadosPainelApi,
  ConsultaCarteira,
  ConsultorApi,
  EstruturaPainelApi,
  FichaLojaApi,
  IndicadoresConsultorApi,
  IndicadoresPainelApi,
  IndicadoresRegionalApi,
  RegionalApi,
  RespostaApi
} from "../types/painelConsultor.api.types";
import type {
  Consultor,
  PainelConsultorDados,
  PosVenda,
  Regional
} from "../types/painelConsultor.types";

const buscar = async <T>(rota: string): Promise<T> => {
  const { data } = await ApiService.get(rota);
  return (data as RespostaApi<T>).data;
};

/** Pós-venda de um escopo que não veio nos indicadores. */
export const SEM_POS_VENDA: PosVenda = {
  faturamento: { atual: 0, anterior: 0 },
  revisoes: { atual: 0, anterior: 0 },
  tmp: { atual: 0, anterior: 0 },
  tmv: { atual: 0, anterior: 0 },
  series: {
    faturamento: { atual: [], anterior: [] },
    revisoes: { atual: [], anterior: [] },
    tmp: { atual: [], anterior: [] },
    tmv: { atual: [], anterior: [] }
  }
};

const SEM_INDICADORES: Omit<IndicadoresConsultorApi, "codConsultor"> = {
  visitasMes: 0,
  visitasMesAnterior: 0,
  posVenda: SEM_POS_VENDA,
  carteira: {
    lojas: 0,
    semVisita: 0,
    visitadasNoMes: 0,
    chamadosAbertos: 0,
    prioridades: {
      semVisita: { total: 0, lojas: [] },
      queda: { total: 0, lojas: [] }
    },
    destaques: null
  }
};

/**
 * Os chamados vêm do Jira, que pode estar fora do ar: a falha vira `null` e só
 * o bloco de chamados fica indisponível, sem derrubar o painel.
 */
const buscarChamados = (): Promise<ChamadosPainelApi | null> =>
  buscar<ChamadosPainelApi>(ROTAS_API_PAINEL.chamados).catch(() => null);

/** Cadastro + números de cada consultor; sem números, entra zerado. */
const juntarIndicadores = (
  consultores: ConsultorApi[],
  indicadores: IndicadoresConsultorApi[]
): Consultor[] => {
  const porConsultor = new Map(
    indicadores.map((item) => [item.codConsultor, item])
  );

  return consultores.map((consultor) => ({
    ...SEM_INDICADORES,
    ...porConsultor.get(consultor.codConsultor),
    ...consultor
  }));
};

/** Cadastro + pós-venda de cada regional; sem números, entra zerada. */
const juntarRegionais = (
  regionais: RegionalApi[],
  indicadores: IndicadoresRegionalApi[]
): Regional[] => {
  const porRegional = new Map(
    indicadores.map((item) => [item.codRegional, item.posVenda])
  );

  return regionais.map((regional) => ({
    ...regional,
    posVenda: porRegional.get(regional.codRegional) ?? SEM_POS_VENDA
  }));
};

/**
 * Único ponto de entrada dos dados do painel: chama as rotas em paralelo e
 * monta o `PainelConsultorDados` que as telas leem. Se uma rota falhar, o
 * painel mostra o erro com "Tentar novamente"; a de chamados é a exceção
 * (buscarChamados). A carteira não vem aqui: é paginada (buscarCarteira), e os
 * números dela chegam resumidos por consultor nos indicadores.
 */
export const buscarDadosPainel = async (): Promise<PainelConsultorDados> => {
  const [estrutura, indicadores, chamados, atas] = await Promise.all([
    buscar<EstruturaPainelApi>(ROTAS_API_PAINEL.estrutura),
    buscar<IndicadoresPainelApi>(ROTAS_API_PAINEL.indicadores),
    buscarChamados(),
    buscar<AtasPainelApi>(ROTAS_API_PAINEL.atas)
  ]);

  return {
    atualizadoEm: indicadores.atualizadoEm,
    dataReferencia: estrutura.dataReferencia,
    regionais: juntarRegionais(estrutura.regionais, indicadores.regionais),
    consultores: juntarIndicadores(
      estrutura.consultores,
      indicadores.consultores
    ),
    posVenda: indicadores.total,
    chamados,
    atas
  };
};

/** Uma página da carteira, buscada, filtrada e ordenada pela API. */
export const buscarCarteira = (
  consulta: ConsultaCarteira
): Promise<CarteiraPainelApi> => {
  const parametros = new URLSearchParams();

  Object.entries(consulta).forEach(([chave, valor]) => {
    if (valor !== undefined && valor !== "") {
      parametros.append(chave, String(valor));
    }
  });

  return buscar<CarteiraPainelApi>(
    `${ROTAS_API_PAINEL.carteira}?${parametros.toString()}`
  );
};

/** A ficha de uma loja da carteira, no escopo de quem está logado. */
export const buscarLoja = (codConcessionaria: number): Promise<FichaLojaApi> =>
  buscar<FichaLojaApi>(ROTAS_API_PAINEL.loja(codConcessionaria));

/** Uma ata completa (relato, motivo, fotos), no escopo de quem está logado. */
export const buscarAta = (codAta: number): Promise<AtaDetalheApi> =>
  buscar<AtaDetalheApi>(ROTAS_API_PAINEL.ata(codAta));
